import { defineStore } from 'pinia';
import PublicService from '@/services/public.service';
import Activite from '@/classes/Activites/Activite.class';

export const useActivitesStore = defineStore('Activites', {
    state: () => ({
        id_module: null,
        list: [],
    }),
    actions: {
        /**
         * Fetches all activities for the current module and updates the store with the retrieved data.
         * 
         * @async
         * @function fetchAll
         * @returns {Promise<void>} A promise that resolves when the activities have been fetched and the store has been updated.
         */
        async fetchAll() {
            this.list = [];
            let res = await PublicService.getActivites(this.id_module);
            if (res) {
                this.set(res);
            }
        },
        /**
         * Sets the list of activities.
         * 
         * @param {Array} data - An array of activity data objects.
         */
        set(data) {
            this.list = [];
            for (let i = 0; i < data.length; i++) {
                let activite = new Activite();
                activite.set(data[i]);
                this.list.push(activite);
            }
        }
    },
    getters: {
        /**
         * Calculates the total number of inscriptions.
         *
         * Iterates through the list of activities and sums up the number of inscriptions for each activity.
         *
         * @returns {number} The total number of inscriptions.
         */
        total_inscriptions() {
            let total = 0;
            for (let i = 0; i < this.list.length; i++) {
                total += parseInt(this.list[i].nb_inscrits);
            }
            return total;
        },
        /**
         * Filters the list of activities to return only those that are visible on the homepage.
         *
         * @returns {Array} An array of activities that have `visible_accueil` set to '1'.
         */
        visibles_accueil() {
            return this.list.filter(activite => activite.visible_accueil == '1');
        },
        /**
         * Groups visible activities by their metadata group.
         *
         * This method iterates over the list of activities and groups them based on the
         * metadata value "groupe_activite". If an activity is not visible (i.e., its
         * `visible_accueil` property is '0'), it is skipped. If an activity does not have
         * a group, it is assigned to the "Sans groupe" group.
         *
         * @returns {Array<Object>} An array of group objects, each containing a `name` property
         *                          for the group name and a `list` property with the activities
         *                          belonging to that group.
         */
        visibles_accueil_grouped() {
            let groups = [];
            for (let i = 0; i < this.list.length; i++) {
                let activite = this.list[i];
                if (activite.visible_accueil == '0') {
                    continue;
                }
                let group = activite.get_metadata("groupe_activite");
                if (group == false) {
                    group = "Sans groupe";
                }
                let group_found = false;
                for (let j = 0; j < groups.length; j++) {
                    if (groups[j].name == group) {
                        groups[j].list.push(activite);
                        group_found = true;
                        break;
                    }
                }
                if (!group_found) {
                    groups.push({ name: group, list: [activite] });
                }
            }
            return groups;
        }
    },
});